import { isAfter } from 'date-fns';
import {
  Control,
  Controller,
  FieldValues,
  useController,
  useWatch,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FunctionRuntime, Hook } from '@rossum/api-client/hooks';
import { User } from '@rossum/api-client/users';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
} from '@rossum/ui/icons/tabler';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  SvgIcon,
  Switch,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../../../components/ReactHookForm/controls/TextFieldControl';
import { docsLinks } from '../../../../../constants/values';
import { link } from '../../../../../lib/formaterValues';
import { getRoleName } from '../../../../../redux/modules/groups/helpers';
import {
  groupSelector,
  isUserAdmin,
  isUserInDifferentOrganization,
} from '../../../../../redux/modules/user/selectors';
import { sideloadOptions } from '../../../../../types/extensions';
import { isPublicFunction } from '../../../helpers';
import { useJSONfield } from '../../../lib/useJSONfield';
import {
  runtimesConfig,
  runtimeTypesConfig,
} from '../../extension-detail/config';
import { TokenOwnerSelect } from '../../extensions-page/components/TokenOwnerSelect';
import { useTemplateWithTokenOwner } from '../hooks/useTemplateWithTokenOwner';
import { DeprecatedRuntimeAlert } from './DeprecatedRuntimeAlert';
import { JsonInput } from './JsonInput';
import { RuntimeChangeAlert } from './RuntimeChangeAlert';

type Props<T extends FieldValues> = {
  allOrgUsers: Array<User>;
  users: Array<User>;
  settingsJSONField: ReturnType<typeof useJSONfield>;
  secretsJSONField: ReturnType<typeof useJSONfield>;
  setShowAdvancedSettings: (_show: boolean) => void;
  showAdvancedSettings: boolean;
  control: Control<T>;
  isFromStore?: boolean;
  readMoreUrl?: string | null;
  name?: string;
  selectedExtension?: Hook;
  showSecretsSection?: boolean;
};

const SETTINGS_INPUT_HEIGHT = 320;
const SECRETS_INPUT_HEIGHT = 121;

export const AdvancedSettings = <T extends FieldValues>({
  allOrgUsers,
  users,
  settingsJSONField,
  secretsJSONField,
  setShowAdvancedSettings,
  showAdvancedSettings,
  control,
  isFromStore,
  readMoreUrl,
  name,
  selectedExtension,
  showSecretsSection,
}: Props<T>) => {
  // WORKAROUND
  // When I tried to add this as a constraint to the generic parameter:
  // T extends { sideload: any[], tokenOwner: any[] } it doesn't work
  const controlTyped = control as unknown as Control<{
    sideload: string[];
    tokenOwner: string | null;
    runtime: FunctionRuntime | undefined;
    payloadLoggingEnabled: boolean;
  }>;

  const {
    field: { onChange: onChangeSideload, value: sideloadValue },
  } = useController({ name: 'sideload', control: controlTyped });

  const {
    field: { value: runtime },
  } = useController({ name: 'runtime', control: controlTyped });

  const {
    field: { value: payloadLoggingEnabled, onChange: onPayloadLoggingChange },
  } = useController({
    name: 'payloadLoggingEnabled',
    control: controlTyped,
  });

  const userFromDifferentOrg = useSelector(isUserInDifferentOrganization);
  const isUserOrgGroupAdmin = useSelector(isUserAdmin);
  const canSetTokenOwner = isFromStore
    ? // Allow to select tokenOwner for (org group) admins and cross group support access even for Store extensions
      isUserOrgGroupAdmin || userFromDifferentOrg
    : true;

  const intl = useIntl();

  const settingsLabel = intl.formatMessage({
    id: 'containers.settings.extensions.createExtension.configuration.label',
  });

  const isExtensionPublicFunction =
    selectedExtension && isPublicFunction(selectedExtension);
  const selectedRuntimeType =
    (isExtensionPublicFunction &&
      runtimesConfig[selectedExtension.config.runtime].type) ??
    null;

  const functionRuntimeConfig =
    isExtensionPublicFunction &&
    runtimesConfig[selectedExtension.config.runtime];

  const selectedRuntimeDeprecationDate =
    (functionRuntimeConfig &&
      'deprecationDate' in functionRuntimeConfig &&
      functionRuntimeConfig.deprecationDate) ??
    undefined;

  const groups = useSelector(groupSelector);

  //  AC-5721: filter out org group admins
  const withTokenOwnersPermission = users.filter(
    user => getRoleName(user.groups, groups) !== 'organization_group_admin'
  );

  const needsTokenOwner = useTemplateWithTokenOwner(
    !!isFromStore,
    selectedExtension?.hookTemplate ?? null
  );

  const tokenOwnerSelected = !!useWatch({
    control: controlTyped,
    name: 'tokenOwner',
  });

  return (
    <Stack alignItems="center" spacing={4}>
      {!isFromStore && (
        <Box>
          <Button
            sx={{ my: 2, width: 'auto' }}
            variant="outlined"
            color="secondary"
            startIcon={
              <SvgIcon fontSize="large">
                {showAdvancedSettings ? <IconChevronUp /> : <IconChevronDown />}
              </SvgIcon>
            }
            data-cy="extensions-show-advanced-settings"
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
          >
            <FormattedMessage id="containers.settings.extensions.createExtension.button.showAdvanced" />
          </Button>
        </Box>
      )}
      {(isFromStore || showAdvancedSettings) && (
        <>
          {!isFromStore && (
            <Stack spacing={1} width={1}>
              <Stack alignItems="flex-start" spacing={1}>
                <Typography variant="body2">
                  <FormattedMessage id="containers.settings.extensions.createExtension.additionalMetadata.label" />
                </Typography>
                <Typography
                  variant="caption"
                  color="secondary"
                  sx={{
                    a: {
                      color: t => t.palette.text.primary,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <FormattedMessage
                    id="containers.settings.extensions.createExtension.additionalMetadata.subLabel"
                    values={{ link: link(docsLinks.webhookVsConnector) }}
                  />
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {sideloadOptions.map(option => (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    key={option}
                  >
                    <Switch
                      data-cy="extensions-advanced-sideload-toggle"
                      checked={sideloadValue.includes(option)}
                      onChange={() => {
                        const newSideload = sideloadValue.includes(option)
                          ? [...sideloadValue.filter(opt => opt !== option)]
                          : [...sideloadValue, option];
                        return onChangeSideload(newSideload);
                      }}
                    />
                    <Typography variant="body2">
                      <FormattedMessage
                        id={`containers.settings.extensions.createExtension.additionalMetadata.${option}`}
                      />
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
          {isExtensionPublicFunction &&
            selectedRuntimeType &&
            runtimeTypesConfig[selectedRuntimeType].options.length > 1 && (
              <Stack spacing={1} width={1}>
                <Stack alignItems="flex-start" spacing={1}>
                  <Typography variant="body2">
                    <FormattedMessage id="containers.settings.extensions.function.runtime.label" />
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    <FormattedMessage id="containers.settings.extensions.function.runtime.subLabel" />
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  <TextFieldControl
                    ControllerProps={{
                      control: controlTyped,
                      name: 'runtime',
                    }}
                    select
                  >
                    {runtimeTypesConfig[selectedRuntimeType].options.map(
                      runtimeVersion => {
                        const runtimeVersionConfig =
                          runtimesConfig[runtimeVersion];
                        const runtimeDeprecationDate =
                          'deprecationDate' in runtimeVersionConfig &&
                          runtimeVersionConfig.deprecationDate;
                        const optionDisabled =
                          !!runtimeDeprecationDate &&
                          isAfter(new Date(Date.now()), runtimeDeprecationDate);

                        return (
                          <MenuItem
                            key={runtimeVersion}
                            value={runtimeVersion}
                            disabled={optionDisabled}
                          >
                            <Stack
                              spacing={1}
                              alignItems="center"
                              direction="row"
                            >
                              <span>{runtimeVersion}</span>
                              {!!runtimeDeprecationDate && (
                                <Tooltip
                                  // do not allow to click on the tooltip when the option is disabled
                                  disableInteractive={optionDisabled}
                                  title={intl.formatMessage(
                                    {
                                      id: 'containers.settings.extensions.function.runtime.deprecatedWarning.tooltip',
                                    },
                                    {
                                      deprecatedDate: intl.formatDate(
                                        runtimeDeprecationDate,
                                        { dateStyle: 'medium' }
                                      ),
                                    }
                                  )}
                                >
                                  <SvgIcon
                                    fontSize="medium"
                                    color="warning"
                                    sx={{
                                      // open tooltip eventhough the MenuItem is disabled
                                      pointerEvents: 'all',
                                    }}
                                    onClick={e => {
                                      // don't trigger the MenuItem onClick for disabled options
                                      if (optionDisabled) {
                                        e.stopPropagation();
                                      }
                                    }}
                                  >
                                    <IconAlertTriangle />
                                  </SvgIcon>
                                </Tooltip>
                              )}
                            </Stack>
                          </MenuItem>
                        );
                      }
                    )}
                  </TextFieldControl>
                  {selectedExtension.config.runtime !== runtime && (
                    <RuntimeChangeAlert />
                  )}
                  {!!selectedRuntimeDeprecationDate && (
                    <DeprecatedRuntimeAlert
                      selectedRuntime={selectedExtension.config.runtime}
                      runtimeDeprecationDate={selectedRuntimeDeprecationDate}
                    />
                  )}
                </Stack>
              </Stack>
            )}
          {canSetTokenOwner && (
            <Stack spacing={1} width={1}>
              <Stack
                alignItems="flex-start"
                direction="row"
                justifyContent="space-between"
              >
                <Stack alignItems="flex-start" spacing={1}>
                  <Typography variant="body2">
                    <FormattedMessage id="containers.settings.extensions.createExtension.tokenOwner.label" />
                  </Typography>
                  <Typography
                    variant="body2"
                    color="secondary"
                    sx={{
                      a: {
                        color: t => t.palette.text.primary,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    <FormattedMessage
                      id="containers.settings.extensions.createExtension.tokenOwner.subLabel"
                      values={{ link: link(docsLinks.tokenOwner) }}
                    />
                  </Typography>
                </Stack>
                {!needsTokenOwner && (
                  <Typography variant="body2" color="secondary">
                    <FormattedMessage id="containers.settings.extensions.createExtension.description.fieldDescription" />
                  </Typography>
                )}
              </Stack>
              <Controller
                control={controlTyped}
                name="tokenOwner"
                render={({ field }) => (
                  <TokenOwnerSelect
                    {...field}
                    allOrgUsers={allOrgUsers}
                    users={withTokenOwnersPermission}
                  />
                )}
              />
              {needsTokenOwner && !tokenOwnerSelected && (
                <Alert
                  variant="outlined"
                  severity="warning"
                  sx={{ mt: 1, mb: 2 }}
                >
                  {intl.formatMessage({
                    id: 'containers.settings.extensions.createExtension.tokenOwner.needToSelect',
                  })}
                </Alert>
              )}
            </Stack>
          )}
          <Stack spacing={1} width={1}>
            <Stack alignItems="flex-start" spacing={1}>
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'containers.settings.extensions.createExtension.payloadLoggingEnabled.title',
                })}
              </Typography>
              <Typography variant="caption" color="secondary">
                {intl.formatMessage({
                  id: 'containers.settings.extensions.createExtension.payloadLoggingEnabled.description',
                })}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                data-cy="payload-logging-enabled"
                checked={payloadLoggingEnabled}
                onChange={(_, value) => onPayloadLoggingChange(value)}
              />
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'containers.settings.extensions.createExtension.payloadLoggingEnabled.label',
                })}
              </Typography>
            </Stack>
          </Stack>
          <Stack spacing={1} width={1}>
            <Stack alignItems="flex-start" spacing={1}>
              <Typography variant="body2">{settingsLabel}</Typography>
              <Typography
                variant="caption"
                color="secondary"
                sx={{
                  a: {
                    color: t => t.palette.text.primary,
                    textDecoration: 'underline',
                  },
                }}
              >
                <FormattedMessage
                  id={
                    showSecretsSection
                      ? 'containers.settings.extensions.createExtension.configuration.subLabelNoSecrets'
                      : 'containers.settings.extensions.createExtension.configuration.subLabel'
                  }
                  values={{
                    link: link(
                      isFromStore && readMoreUrl
                        ? readMoreUrl
                        : docsLinks.hookConfiguration
                    ),
                  }}
                />
              </Typography>
            </Stack>
            <JsonInput
              name="extensions-settings-input"
              height={SETTINGS_INPUT_HEIGHT}
              jsonFieldProps={settingsJSONField}
              title={name ? [name, settingsLabel].join(' ') : settingsLabel}
              expandable
            />
          </Stack>
          {showSecretsSection && (
            <Stack spacing={1} width={1}>
              <Stack alignItems="flex-start" spacing={1}>
                <Typography variant="body2">
                  {intl.formatMessage({
                    id: 'containers.settings.extensions.createExtension.secrets.label',
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  color="secondary"
                  sx={{
                    a: {
                      color: t => t.palette.text.primary,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <FormattedMessage
                    id="containers.settings.extensions.createExtension.secrets.subLabel"
                    values={{
                      link: link(docsLinks.hookSecrets),
                    }}
                  />
                </Typography>
              </Stack>
              <JsonInput
                name="extensions-secrets-input"
                height={SECRETS_INPUT_HEIGHT}
                jsonFieldProps={secretsJSONField}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};
